// features/workstations/dashboard-sections/news-correlation.js
// ═══════════════════════════════════════════════════════════
// NEWS CORRELATION — which feed items mention YOUR tickers
// ═══════════════════════════════════════════════════════════
// Tier D day-trading. Reads recent intelligence_feed_items in
// the workstation’s domain, scans each for the user’s tickers
// (config.criteria.instruments ∪ workstation_watchlist_entities),
// and renders matches grouped by ticker.
//
// V1 is regex-only — fast, deterministic, no AI cost. The
// follow-up layer adds Sonnet classification of bullish/bearish
// + impact + reason once the cache table is wired (separate batch).
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PillRow,
  PillTable,
  PractitionerLens,
  MetaField,
} from '../../../components/widgets/primitives';

const ITEM_TYPE_TONES = {
  news:          'steel',
  breakthrough:  'gold',
  signal_watch:  'amber',
};

const IMPORTANCE_TONES = {
  high:    'terra',
  medium:  'amber',
  low:     'steel',
};

// ═══════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════
export default function NewsCorrelationSection({ workstation, userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hours, setHours] = useState(48);
  const [filterTicker, setFilterTicker] = useState(null);

  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams({
        workstation_id: workstation.id,
        user_id: userId,
        hours: String(hours),
      });
      const res = await fetch(`/api/workstations/news-correlation?${qs}`, { cache: 'no-store' });
      const payload = res.ok ? await res.json() : null;
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      setData(payload || null);
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId, hours]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <section className="ws-news">
        <div className="ws-news-loading">Scanning feed…</div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="ws-news">
        <Eyebrow>news correlation — read failed</Eyebrow>
        <SerifSummary>{error || 'No data returned.'}</SerifSummary>
      </section>
    );
  }

  return (
    <NewsBody
      data={data}
      hours={hours}
      onHoursChange={setHours}
      filterTicker={filterTicker}
      onFilterTickerChange={setFilterTicker}
    />
  );
}

// ═══════════════════════════════════════════════════════════
// BODY
// ═══════════════════════════════════════════════════════════
function NewsBody({ data, hours, onHoursChange, filterTicker, onFilterTickerChange }) {
  const items = Array.isArray(data.items) ? data.items : [];
  const tickerCounts = data.ticker_counts || {};
  const sortedTickers = Object.entries(tickerCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([t]) => t);

  // Filter items if a ticker is selected
  const visible = filterTicker
    ? items.filter(i => (i.matched_instruments || []).includes(filterTicker))
    : items;

  const headline = data.no_tickers
    ? 'No tickers configured.'
    : items.length === 0
      ? 'No matches in the feed window.'
      : `${items.length} item${items.length === 1 ? '' : 's'} mention your ${sortedTickers.length} ticker${sortedTickers.length === 1 ? '' : 's'}`;
  const headlineTone = data.no_tickers ? 'steel'
    : items.length === 0 ? 'steel'
    : 'sage';

  const summary = data.no_tickers
    ? data.hint || 'Add tickers to your workstation criteria or watchlist.'
    : items.length === 0
      ? `Scanned ${data.total_feed_scanned || 0} items in the last ${hours}h — none touched your names. Try a longer window.`
      : `${data.total_feed_scanned || 0} items scanned in the last ${hours}h. Click a ticker to filter.`;

  return (
    <section className="ws-news">
      <Eyebrow parts={[
        'news correlation',
        `${hours}h window`,
        items.length > 0 ? `${items.length} matched` : 'no matches',
      ]} />

      <SerifHeadline>
        <span className={`ws-news-tone-${headlineTone}`}>{headline}</span>
      </SerifHeadline>
      <SerifSummary>{summary}</SerifSummary>

      {/* Window chips */}
      <div className="ws-news-action-row">
        <div className="ws-news-window-chips">
          <span className="ws-news-window-label">window</span>
          {[12, 24, 48, 72, 168].map((h) => (
            <button
              key={h}
              type="button"
              className={`ws-news-window-chip ${h === hours ? 'is-active' : ''}`}
              onClick={() => onHoursChange(h)}
            >
              {h < 24 ? `${h}h` : h === 168 ? '7d' : `${h / 24}d`}
            </button>
          ))}
        </div>
      </div>

      {/* Ticker pills (filter) */}
      {sortedTickers.length > 0 && (
        <SectionStrip label="your tickers" count={sortedTickers.length}>
          <div className="ws-news-ticker-pills">
            <button
              type="button"
              className={`ws-news-ticker-pill ${!filterTicker ? 'is-active' : ''}`}
              onClick={() => onFilterTickerChange(null)}
            >
              all
            </button>
            {sortedTickers.map((t) => (
              <button
                key={t}
                type="button"
                className={`ws-news-ticker-pill ${filterTicker === t ? 'is-active' : ''}`}
                onClick={() => onFilterTickerChange(filterTicker === t ? null : t)}
              >
                <span className="ws-news-ticker-name">{t}</span>
                <span className="ws-news-ticker-count">{tickerCounts[t]}</span>
              </button>
            ))}
          </div>
        </SectionStrip>
      )}

      {/* Matched feed items */}
      {visible.length > 0 && (
        <SectionStrip
          label={filterTicker ? `${filterTicker} — matches` : 'matched items'}
          count={visible.length}
        >
          <div className="ws-news-list">
            {visible.map((item) => (
              <NewsItemCard key={item.id} item={item} />
            ))}
          </div>
        </SectionStrip>
      )}

      <PractitionerLens label="what this widget gives you (and doesn’t)">
        V1 is a word-boundary scan — fast and deterministic. It tells
        you WHICH items touch your names. It does NOT yet score
        bullish/bearish or impact — that's an AI layer in V2.
        Use this to spot the tickers you should look at first.
      </PractitionerLens>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// NEWS ITEM CARD
// ═══════════════════════════════════════════════════════════
function NewsItemCard({ item }) {
  const [expanded, setExpanded] = useState(false);
  const typeTone = ITEM_TYPE_TONES[item.item_type] || 'steel';
  const importanceTone = item.importance ? IMPORTANCE_TONES[item.importance] || 'steel' : null;

  return (
    <div className={`ws-news-card ws-news-card-${typeTone}`}>
      <button
        type="button"
        className="ws-news-card-head"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="ws-news-card-row">
          <span className={`ws-news-type ws-news-type-${typeTone}`}>{item.item_type || 'news'}</span>
          {item.importance && (
            <span className={`ws-news-importance ws-news-importance-${importanceTone}`}>
              {item.importance}
            </span>
          )}
          <div className="ws-news-matched">
            {(item.matched_instruments || []).slice(0, 6).map((t) => (
              <span key={t} className="ws-news-matched-pill">{t}</span>
            ))}
          </div>
          <span className="ws-news-when">{fmtWhen(item.published_at)}</span>
        </div>
        <div className="ws-news-headline">{item.headline}</div>
      </button>

      {expanded && (
        <div className="ws-news-drawer">
          {item.description && (
            <div className="ws-news-description">{item.description}</div>
          )}
          {item.signal_basis && (
            <MetaField label="signal basis">{item.signal_basis}</MetaField>
          )}
          {item.source_name && (
            <MetaField label="source">{item.source_name}</MetaField>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ───
function fmtWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffH = Math.floor(diffMs / 3600000);
    if (diffH < 1) {
      const min = Math.max(1, Math.floor(diffMs / 60000));
      return `${min}m`;
    }
    if (diffH < 24) return `${diffH}h`;
    const diffDays = Math.floor(diffH / 24);
    return `${diffDays}d`;
  } catch {
    return '';
  }
}
